import { z } from "zod";

/**
 * Response contracts for gft-insights-api.
 * Keep in sync with the lambda handlers in that repo
 * (org convention is copy-paste, not a shared package).
 */

export const MAX_UPCS = 100;

// --- Ops: platform statistics (store/lane health) ---

export const StoreStatusRowSchema = z.object({
  storeName: z.string(),
  posVendor: z.string().nullable(), // partner that installed the plugin
  waterfallFeesPerTransaction: z.number(), // $/txn: agreement amounts + the flat $0.10 store cut
  profitPerTransaction: z.number(), // revenuePerTransaction - waterfallFeesPerTransaction
  lastPingedAt: z.string().nullable(), // ISO timestamp
  lastCheckoutAt: z.string().nullable(),
  laneCount: z.number(),
  activeCampaignCount: z.number(),
  totalTransactions: z.number(),
});
export type StoreStatusRow = z.infer<typeof StoreStatusRowSchema>;

export const LaneStatusRowSchema = z.object({
  storeName: z.string(),
  lane: z.string(), // lane number, or "default"
  pluginId: z.string().nullable(),
  lastPingedAt: z.string().nullable(),
  lastCheckoutAt: z.string().nullable(),
  totalTransactions: z.number(),
});
export type LaneStatusRow = z.infer<typeof LaneStatusRowSchema>;

export const StoreDmaUnitsRowSchema = z.object({
  dma: z.string(),
  storeName: z.string(),
  units: z.number(), // units sold, last 30 days
});
export type StoreDmaUnitsRow = z.infer<typeof StoreDmaUnitsRowSchema>;

export const PlatformStatsSchema = z.object({
  generatedAt: z.string(),
  revenuePerTransaction: z.number(), // flat GFT revenue per transaction ($)
  tiles: z.object({
    // Totals across all time, unlike the 30-day tiles below, over shoppers
    // who have a home store.
    shoppersWithPhoneAllTime: z.number(), // a phone number is what makes a shopper registered
    shoppersWithWalletAllTime: z.number(), // has a blockchain wallet (eth address)
    shoppersOptedInForMarketingAllTime: z.number(), // marketing_opt_in is on today
    storeCount: z.number(),
    storesPingedLastDay: z.number(),
    storesCheckedOutLastDay: z.number(),
    laneCount: z.number(),
    transactionCount30d: z.number(),
    identifiedTransactionCount30d: z.number(),
    uniqueIdentifiedUsers30d: z.number(),
  }),
  storeStatus: z.array(StoreStatusRowSchema),
  laneStatus: z.array(LaneStatusRowSchema),
  storesByDma: z.array(StoreDmaUnitsRowSchema),
});
export type PlatformStats = z.infer<typeof PlatformStatsSchema>;

// --- Ops: user phone lookup ---

export const UserPhoneSchema = z.object({
  userId: z.string(),
  phoneNumber: z.string().nullable(),
});
export type UserPhone = z.infer<typeof UserPhoneSchema>;

// --- Ops: campaign & redemption statistics ---

export const CampaignRowSchema = z.object({
  campaignName: z.string(),
  startDate: z.string(), // ISO date
  endDate: z.string(),
  campaignType: z.string(), // e.g. DOLLAR_OFF, TPR
  rewardValue: z.number(),
  redemptions: z.number(),
  campaignSpend: z.number(),
  campaignBudget: z.number().nullable(),
});
export type CampaignRow = z.infer<typeof CampaignRowSchema>;

export const StoreCampaignRedemptionsRowSchema = z.object({
  storeName: z.string(),
  campaignName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  redemptions: z.number(),
  earnedByStore: z.number(),
  totalPaidToStore: z.number(),
});
export type StoreCampaignRedemptionsRow = z.infer<
  typeof StoreCampaignRedemptionsRowSchema
>;

export const CampaignUpcRowSchema = z.object({
  campaignName: z.string(),
  upc: z.string().nullable(),
  brandName: z.string().nullable(),
  subcategoryName: z.string().nullable(),
  dma: z.string().nullable(),
});
export type CampaignUpcRow = z.infer<typeof CampaignUpcRowSchema>;

export const CampaignUpcsSchema = z.object({
  generatedAt: z.string(),
  upcs: z.array(CampaignUpcRowSchema),
});
export type CampaignUpcs = z.infer<typeof CampaignUpcsSchema>;

export const CampaignStatsSchema = z.object({
  generatedAt: z.string(),
  redemptionsOverTime: z.array(
    z.object({ day: z.string(), redemptions: z.number() }),
  ),
  redemptionsByDma: z.array(
    z.object({ dma: z.string(), redemptions: z.number() }),
  ),
  campaigns: z.array(CampaignRowSchema),
  redemptionsByCampaignByStore: z.array(StoreCampaignRedemptionsRowSchema),
});
export type CampaignStats = z.infer<typeof CampaignStatsSchema>;

// --- Ops: retailer insights ---

// Same row shape as the ops campaign dashboard's store x campaign table.
export const RetailerRedemptionRowSchema = StoreCampaignRedemptionsRowSchema;
export type RetailerRedemptionRow = z.infer<typeof RetailerRedemptionRowSchema>;

export const RetailerRedemptionsSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  redemptionsByCampaign: z.array(RetailerRedemptionRowSchema),
  redemptionsOverTime: z.array(
    z.object({ day: z.string(), redemptions: z.number() }),
  ),
});
export type RetailerRedemptions = z.infer<typeof RetailerRedemptionsSchema>;

export const RetailerEngagementRowSchema = z.object({
  bucket: z.string(), 
  shoppers: z.number(),
  transactions: z.number(),
  units: z.number(),
});
export type RetailerEngagementRow = z.infer<typeof RetailerEngagementRowSchema>;

export const RetailerOverviewSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  tiles: z.object({
    totalTransactions: z.number(),
    loyaltyTransactions: z.number(),
    totalUnits: z.number(),
    estimatedShoppers: z.number(), // distinct transactions / 24 heuristic
    loyaltyShoppers: z.number(),
    totalSales: z.number(),
  }),
  engagement: z.array(RetailerEngagementRowSchema),
});
export type RetailerOverview = z.infer<typeof RetailerOverviewSchema>;

export const RetailerTrafficSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  // Sparse: only non-empty cells; dayOfWeek 1=Monday..7=Sunday (America/New_York)
  cells: z.array(
    z.object({
      dayOfWeek: z.number(),
      hour: z.number(),
      units: z.number(),
    }),
  ),
});
export type RetailerTraffic = z.infer<typeof RetailerTrafficSchema>;

export const RetailerCategoryRowSchema = z.object({
  category: z.string(),
  units: z.number(),
  sales: z.number(),
});
export type RetailerCategoryRow = z.infer<typeof RetailerCategoryRowSchema>;

export const RetailerCategoriesSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  categories: z.array(RetailerCategoryRowSchema),
});
export type RetailerCategories = z.infer<typeof RetailerCategoriesSchema>;

export const RetailerProductRowSchema = z.object({
  upc: z.string(),
  productName: z.string().nullable(),
  units: z.number(),
});
export type RetailerProductRow = z.infer<typeof RetailerProductRowSchema>;

export const RetailerProductsSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  rankLimit: z.number(),
  top: z.array(RetailerProductRowSchema),
  bottom: z.array(RetailerProductRowSchema),
});
export type RetailerProducts = z.infer<typeof RetailerProductsSchema>;

// --- Retailer insights: shopper growth (registrations + marketing opt-ins by signup week) ---

export const ShopperGrowthWeekSchema = z.object({
  weekStart: z.string(), // ISO date, Monday (UTC)
  registered: z.number(), // shoppers whose account was created this week
  optedIn: z.number(), // of those, currently opted in to marketing
});
export type ShopperGrowthWeek = z.infer<typeof ShopperGrowthWeekSchema>;

export const ShopperGrowthStoreRowSchema = z.object({
  storeId: z.string(),
  storeName: z.string(),
  registered: z.number(), // all time
  optedIn: z.number(), // all time, currently opted in
  periodRegistered: z.number(), // signed up inside the window
  periodOptedIn: z.number(),
});
export type ShopperGrowthStoreRow = z.infer<typeof ShopperGrowthStoreRowSchema>;

export const ShopperGrowthSchema = z.object({
  generatedAt: z.string(),
  windowWeeks: z.number(), // complete weeks before the current one
  windowStart: z.string(), // ISO date: Monday of the first week in `weekly`
  currentWeekStart: z.string(), // ISO date: Monday of the current week — the last, partial entry
  // Everything before windowStart, so running totals start from all time.
  baseline: z.object({ registered: z.number(), optedIn: z.number() }),
  weekly: z.array(ShopperGrowthWeekSchema), // windowWeeks + 1 entries, zero-filled; last = current week to date
  stores: z.array(ShopperGrowthStoreRowSchema),
});
export type ShopperGrowth = z.infer<typeof ShopperGrowthSchema>;

// --- Ops: product performance insights ---

const CategoryOptionSchema = z.object({
  categoryId: z.string(),
  categoryName: z.string(),
});

export const ProductFiltersSchema = z.object({
  generatedAt: z.string(),
  brands: z.array(z.object({ brandId: z.string(), brandName: z.string() })),
  accounts: z.array(
    z.object({ accountId: z.string(), accountName: z.string() }),
  ),
  dmas: z.array(z.string()),
  categories: z.array(CategoryOptionSchema), // level-2 ("Parent -> Child")
  level1Categories: z.array(CategoryOptionSchema),
  stores: z.array(z.object({ storeId: z.string(), storeName: z.string() })),
  campaigns: z.array(
    z.object({
      campaignId: z.string(),
      campaignName: z.string(),
      accountName: z.string().nullable(), // owning GFT account
      beginDate: z.string(), // ISO date
      endDate: z.string(),
    }),
  ),
});
export type ProductFilters = z.infer<typeof ProductFiltersSchema>;

export const CatalogSearchRowSchema = z.object({
  upc: z.string(),
  checkDigit: z.string().nullable(),
  productName: z.string().nullable(),
  brandName: z.string().nullable(),
  categoryName: z.string().nullable(),
  subcategoryName: z.string().nullable(),
});
export type CatalogSearchRow = z.infer<typeof CatalogSearchRowSchema>;

export const CatalogSearchSchema = z.object({
  generatedAt: z.string(),
  rowLimit: z.number(),
  capped: z.boolean(),
  rows: z.array(CatalogSearchRowSchema),
});
export type CatalogSearch = z.infer<typeof CatalogSearchSchema>;

export const ProductDemandSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  productLimit: z.number(),
  capped: z.boolean(),
  unitsByDay: z.array(z.object({ day: z.string(), units: z.number() })),
  products: z.array(
    z.object({
      upc: z.string(),
      // Catalog name, falling back to the most common register name
      productName: z.string().nullable(),
      units: z.number(),
    }),
  ),
  byStore: z.array(
    z.object({ storeName: z.string(), units: z.number() }),
  ),
  byDma: z.array(z.object({ dma: z.string(), units: z.number() })),
});
export type ProductDemand = z.infer<typeof ProductDemandSchema>;

export const CampaignImpactCampaignSchema = z.object({
  campaignId: z.string(),
  campaignName: z.string(),
  campaignType: z.string(),
  beginDate: z.string(), // ISO date
  endDate: z.string(),
  rewardValue: z.number(),
  budget: z.number().nullable(),
  spend: z.number(),
  redemptions: z.number(),
});
export type CampaignImpactCampaign = z.infer<
  typeof CampaignImpactCampaignSchema
>;

export const CampaignImpactStoreRowSchema = z.object({
  storeId: z.string(),
  storeName: z.string(),
  redemptions: z.number(),
  units: z.number(), // filtered-UPC units in the campaign window
});
export type CampaignImpactStoreRow = z.infer<
  typeof CampaignImpactStoreRowSchema
>;

export const CampaignImpactStoreUpcRowSchema = z.object({
  storeId: z.string(),
  storeName: z.string(),
  upc: z.string(),
  minPrice: z.number(),
  units: z.number(),
});
export type CampaignImpactStoreUpcRow = z.infer<
  typeof CampaignImpactStoreUpcRowSchema
>;

export const CampaignImpactSchema = z.object({
  generatedAt: z.string(),
  baselineDays: z.number(),
  // Trend spans [windowStart, windowEnd]; windowStart = campaignStart minus
  // the baseline. Tiles/tables count [campaignStart, windowEnd] only.
  windowStart: z.string(),
  campaignStart: z.string(),
  // Baseline/campaign boundary: campaignStart or the first redemption day,
  // whichever is earlier. Drives the chart marker and the lift averages.
  liftStart: z.string(),
  windowEnd: z.string(),
  // The request's UPC filter, normalized 13-digit zero-padded. Units fields
  // cover ONLY these UPCs; empty means the units queries were skipped.
  upcs: z.array(z.string()),
  campaigns: z.array(CampaignImpactCampaignSchema),
  totals: z.object({
    redemptions: z.number(),
    uniqueShoppers: z.number(),
    units: z.number(),
    spend: z.number(),
    budget: z.number().nullable(), // sum of set budgets; null when none set
  }),
  // Avg filtered-UPC units/day: pre-campaign baseline vs the campaign run
  // so far. Both zero when no UPCs were requested.
  averageDailyUnits: z.object({
    baseline: z.number(),
    campaign: z.number(),
  }),
  byStore: z.array(CampaignImpactStoreRowSchema),
  byStoreUpc: z.array(CampaignImpactStoreUpcRowSchema),
  trend: z.array(
    z.object({ day: z.string(), redemptions: z.number(), units: z.number() }),
  ),
  spendByDay: z.array(z.object({ day: z.string(), spend: z.number() })),
  redemptionsByDma: z.array(
    z.object({ dma: z.string(), redemptions: z.number() }),
  ),
});
export type CampaignImpact = z.infer<typeof CampaignImpactSchema>;

export const BrandUnitsCellSchema = z.object({
  brandUnits: z.number(),
  categoryUnits: z.number(),
});

export const BrandMetricsSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  totals: BrandUnitsCellSchema,
  basketPenetration: z.number(), // 0..1; 1 when no brand selected
  byWeek: z.array(
    BrandUnitsCellSchema.extend({ weekStart: z.string() }),
  ),
  byDma: z.array(BrandUnitsCellSchema.extend({ dma: z.string() })),
  engagement: z.array(RetailerEngagementRowSchema),
});
export type BrandMetrics = z.infer<typeof BrandMetricsSchema>;

export const UpcMetricsSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  upcs: z.array(z.string()), // normalized 13-digit zero-padded
  unitsByDay: z.array(z.object({ day: z.string(), units: z.number() })),
  byStore: z.array(
    z.object({ storeName: z.string(), units: z.number() }),
  ),
  byStoreUpc: z.array(
    z.object({
      storeName: z.string(),
      upc: z.string(),
      minPrice: z.number(),
      units: z.number(),
    }),
  ),
});
export type UpcMetrics = z.infer<typeof UpcMetricsSchema>;

export const ProductRawRowSchema = z.object({
  brand: z.string().nullable(),
  upc: z.string(),
  productName: z.string().nullable(),
  subcategory: z.string().nullable(),
  dma: z.string().nullable(),
  units: z.number(),
  amount: z.number(),
  minPrice: z.number(),
});
export type ProductRawRow = z.infer<typeof ProductRawRowSchema>;

export const ProductRawSchema = z.object({
  generatedAt: z.string(),
  windowDays: z.number(),
  rowLimit: z.number(),
  capped: z.boolean(),
  rows: z.array(ProductRawRowSchema),
});
export type ProductRaw = z.infer<typeof ProductRawSchema>;
